// ============================================================
// esignRoute.ts — B4c (decision D5): which PUBLIC e-signature page, if any,
// this address is. Pure (esignRoute.test.ts); src/index.tsx asks it BEFORE
// the app decides login vs app, so these pages never show a login screen,
// app chrome or the presence socket.
//
//   sign.<anything> (https://sign.casevo.co.uk — the signing host):
//     /verify           → the verify page
//     /verify/<ref>     → the verify page, that record loaded
//     /seal             → the seal certificate page (E-sign audit fixes A)
//     /s#<token>        → a signer's link — the token in the FRAGMENT, so a
//                         browser never sends it (E-sign audit fixes A)
//     /s/<token>        → a signer's link sent before (still works)
//     anything else     → a small "Casevo e-signature" landing
//   any other host (casevo.vercel.app, localhost …) — the fallback:
//     /sign/…           → the same as above under /sign
//                         (/sign/verify, /sign/verify/<ref>, /sign/seal,
//                         /sign/s#<token>, /sign/s/<token>)
//     /verify, /verify/<ref> → the verify page (a certificate's QR may
//                         point at either host)
//     anything else     → null (the normal app)
// ============================================================

export type PublicEsignRoute =
  | { page: 'verify'; ref?: string }
  | { page: 'sign'; token: string }
  | { page: 'seal' }
  | { page: 'landing' }

const decode = (s: string): string => { try { return decodeURIComponent(s) } catch { return s } }

// (E-sign audit fixes A) The token from "#<token>" ('' when there is none).
export const tokenFromHash = (hash: string | null | undefined): string => decode((hash ?? '').replace(/^#/, '')).trim()

// The path under the public shell's root ('' / '/verify/ABC…' / '/s/tok').
const match = (rest: string, hash: string): PublicEsignRoute => {
  const parts = rest.split('/').filter(Boolean)
  if (parts[0] === 'verify' && parts.length <= 2) {
    // A reference is typed in capitals ([A-Z2-9]); accept lower case too.
    return parts[1] ? { page: 'verify', ref: decode(parts[1]).trim().toUpperCase() } : { page: 'verify' }
  }
  if (parts[0] === 'seal' && parts.length === 1) return { page: 'seal' }
  // /s#<token> (/s with no token → the page says the link isn't valid).
  if (parts[0] === 's' && parts.length === 1) {
    const token = tokenFromHash(hash)
    if (token || !rest.endsWith('/')) return { page: 'sign', token }
  }
  if (parts[0] === 's' && parts.length === 2 && parts[1]) return { page: 'sign', token: decode(parts[1]) }
  return { page: 'landing' }
}

export const isSignHost = (hostname: string): boolean => hostname.toLowerCase().startsWith('sign.')

export const publicEsignRoute = (hostname: string, pathname: string, hash = ''): PublicEsignRoute | null => {
  const path = pathname || '/'
  if (isSignHost(hostname)) {
    // The /sign prefix is harmless on the signing host too.
    return match(/^\/sign(\/|$)/i.test(path) ? path.slice(5) : path, hash)
  }
  if (/^\/sign(\/|$)/.test(path)) return match(path.slice(5), hash)
  if (/^\/verify(\/[^/]*)?\/?$/.test(path)) return match(path, hash)
  return null
}

// Where a page link points within the shell (keeps the /sign prefix on the
// fallback host, none on the signing host).
export const publicEsignHref = (hostname: string, to: '/verify' | `/verify/${string}` | '/seal' | '/'): string =>
  isSignHost(hostname) ? to : `/sign${to === '/' ? '' : to}`
